import { useQuery } from '@apollo/client';
import { FeatureFlagTypes } from '@generated/graphql';
import { FETCH_SUBACCOUNT_CURRENCY_STATS } from '@ovComponents/4-module/workflowCompletion/subSteps/queries';
import { isNearFullWithdrawal } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/depositAmountRules';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { currencySymbol } from '@utils/currency';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { useToast } from 'hephaestus/web';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationFeatureFlag } from '../../../../../../hooks/useOrganizationFeatureFlag';
import {
  type CurrencyBalance,
  getAvailableCurrenciesForTransfer,
  hasInsufficientCurrencyBalance,
  isMultiCurrencyInternalTransfer,
} from './depositInternalTransferCurrencies';

type UseDepositInternalTransferProp = {
  deposit: TransferContextData;
  updateContextWithFields: (
    fields: Partial<TransferContextData>,
  ) => Promise<void>;
};

export const useDepositInternalTransfer = ({
  deposit,
  updateContextWithFields,
}: UseDepositInternalTransferProp) => {
  const { t } = useTranslation('workflowCompletions');
  const { enqueueToast } = useToast();
  const [currencyBalance, setCurrencyBalance] =
    useState<CurrencyBalance | null>(null);

  const enableMultiCurrencyDepositTransfers = useOrganizationFeatureFlag(
    FeatureFlagTypes.MultiCurrencyDepositTransfers,
  );
  const isMultiCurrencyInternal = isMultiCurrencyInternalTransfer({
    transferType: deposit?.type,
    enableMultiCurrencyDepositTransfers,
  });
  const availableCurrencies = getAvailableCurrenciesForTransfer({
    applicableData: deposit,
  });
  const { data: subAccountData } = useQuery(FETCH_SUBACCOUNT_CURRENCY_STATS, {
    variables: {
      subAccountId: deposit?.fromSubAccount?.id,
      currency: deposit?.currency,
    },
    skip:
      !isMultiCurrencyInternal ||
      !deposit?.fromSubAccount?.id ||
      !deposit?.currency,
  });

  useEffect(() => {
    if (subAccountData?.fetchSubAccount?.subAccount?.statistics) {
      const stats = subAccountData.fetchSubAccount.subAccount.statistics;
      const availableCents = stats.availableFundsToWithdrawCents || 0;

      setCurrencyBalance({
        availableCents,
        currency: deposit?.currency || '',
      });
    } else if (deposit?.currency) {
      setCurrencyBalance(null);
    }
  }, [subAccountData, deposit?.currency, deposit?.fromSubAccount?.id]);

  useEffect(() => {
    if (
      !isMultiCurrencyInternal ||
      !deposit?.fromSubAccount?.id ||
      !deposit?.subAccount?.id
    ) {
      return;
    }

    if (
      availableCurrencies.length === 1 &&
      availableCurrencies[0] !== deposit?.currency
    ) {
      updateContextWithFields({ currency: availableCurrencies[0] });
    }
  }, [
    isMultiCurrencyInternal,
    deposit?.fromSubAccount?.id,
    deposit?.subAccount?.id,
  ]);

  const checkInsufficientBalance = () => {
    if (
      !currencyBalance ||
      !hasInsufficientCurrencyBalance({
        isMultiCurrencyInternalTransfer: isMultiCurrencyInternal,
        applicableData: deposit,
        currencyBalance,
      })
    ) {
      return false;
    }

    enqueueToast(
      t('components:transferModal.insufficientCurrencyBalance', {
        currency: currencyBalance.currency,
        available: formatMoneyValue(
          currencyBalance.availableCents,
          currencySymbol[
            currencyBalance.currency as keyof typeof currencySymbol
          ] || '$',
        ),
      }),
      { variant: 'error' },
    );
    return true;
  };

  return {
    enableMultiCurrencyDepositTransfers,
    isMultiCurrencyInternal,
    availableCurrencies,
    currencyBalance,
    amountCurrency:
      enableMultiCurrencyDepositTransfers && deposit?.currency
        ? deposit.currency
        : undefined,
    isNearFullWithdrawal: isNearFullWithdrawal(deposit),
    checkInsufficientBalance,
  };
};
